import React from 'react';
import { History } from 'history';
import { createStore, applyMiddleware, combineReducers } from 'redux';
import { Provider } from 'react-redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import thunk from 'redux-thunk';
import { appContext, AppActions } from 'hellospa/app-context';
import { Store, ExtraArgument } from './types';
import {
  TEMPLATE_NAMESPACE_KEY,
  TS_EXAMPLE_NAMESPACE_KEY,
  MOAR_NAMESPACE_KEY,
  PREP_AND_SEND_NAMESPACE_KEY,
  NOTIFICAION_BANNER_NAMESPACE_KEY,
  ADMIN_GROUP_SETTINGS_NAMESPACE_KEY,
  DOCUMENT_PREVIEW_NAMESPACE_KEY,
} from './namespaces';
import { templateReducer } from 'hellospa/redux/template/data/reducer';
import { tsExampleReducer } from 'hellospa/components/ts-example/data/reducer';
import { moarReducer } from 'hellospa/page/moar/data/reducer';
import { prepAndSendReducer } from 'hellospa/page/prep-and-send/data/reducer';
import { useHistory } from 'react-router';
import { notificationBannerReducer } from 'hellospa/components/notification-banner/data/reducer';
import adminGroupSettingsReducer from 'admin/store/namespaces/group-settings/reducer';
import { documentPreviewReducer } from 'hellospa/components/signer-signature-document/preview/data/reducer';

const extraArguments = {
  appActions: {} as AppActions,
  routerHistory: {} as History,
};
const getExtraArguments: ExtraArgument = () => extraArguments;


let storeSingleton: Store | undefined;
// Exported for use in tests
export function constructStore(getExtra = getExtraArguments): Store {
  const reducer = combineReducers({
    [TEMPLATE_NAMESPACE_KEY]: templateReducer,
    [MOAR_NAMESPACE_KEY]: moarReducer,
    [TS_EXAMPLE_NAMESPACE_KEY]: tsExampleReducer,
    [PREP_AND_SEND_NAMESPACE_KEY]: prepAndSendReducer,
    [NOTIFICAION_BANNER_NAMESPACE_KEY]: notificationBannerReducer,
    [ADMIN_GROUP_SETTINGS_NAMESPACE_KEY]: adminGroupSettingsReducer,
    [DOCUMENT_PREVIEW_NAMESPACE_KEY]: documentPreviewReducer,
  });

  const composeEnhancers = composeWithDevTools({
    name: 'hellosign',
  });
  return createStore(
    reducer,
    composeEnhancers(
      applyMiddleware(
        thunk.withExtraArgument(getExtra),
      ),
    ),
  );
}

type Props = {
  children: React.ReactNode,
  getExtra?: ExtraArgument,
  store?: Store,
}
export default function StoreProvider({ children, getExtra, store = storeSingleton }: Props) {
  const appActions = React.useContext(appContext);
  const routerHistory = useHistory();

  // Only update the extraArguments singleton if this doesn't use a custom getExtra...
  if (!getExtra) {
    // ... and this is, or will use the storeSingleton
    if (store === storeSingleton || !storeSingleton) {
      extraArguments.appActions = appActions;
      extraArguments.routerHistory = routerHistory;
    }
  }


  const storeRef = React.useRef(store);
  if (!storeRef.current) {
    if (getExtra) {
      storeRef.current = constructStore(getExtra);
    } else {
      if (!storeSingleton) {
        storeSingleton = constructStore();
      }
      storeRef.current = storeSingleton;
    }
  }

  return (
    <Provider store={storeRef.current}>{children}</Provider>
  );
}

type TestProps = Props & {
  // pass a mock object for the test
  appActions?: any,
}
export function TestStoreProvider({ children, appActions }: TestProps) {
  const storeRef = React.useRef<Store>();
  if (!storeRef.current) {
    storeRef.current = constructStore(() => ({
      appActions,
      get routerHistory(): History {
        throw new Error("TestStoreProvider doesn't support routerHistory. Use hellospa/test-helpers instead.");
      },
    }));
  }

  return (
    <Provider store={storeRef.current}>{children}</Provider>
  );
}
